import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { registerSW } from 'virtual:pwa-register';

import { RouterProvider } from 'react-router';
import { createRouter } from './shared/routes/router.routes';
import { ThemeProvider } from '@mui/material/styles';
import { Theme } from './shared/config/MaterialUITheme';
import '@denali-design/icons/dist/font/denali-icons-font.css';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MaintenanceProvider } from './shared/contexts/MaintenanceContext';
import { AuthProvider } from './shared/contexts/AuthContext';
import * as Sentry from "@sentry/react";

import { I18nProvider } from './shared/contexts/I18nContext';
import { NotificationProvider } from './shared/contexts/NotificationContext';
import MaintenanceWrapper from './shared/components/MaintenanceWrapper';
import { SENTRY_TRACE_TARGETS } from './shared/config/sentry';
import { sileo, Toaster } from "sileo";
import AppCrashFallback from './shared/components/error-boundaries/AppCrashFallback';

let errorCount = 0;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || '',
  /* 
   Si esta opción está en true, se enviarán datos PII por defecto a Sentry.
   Por ejemplo, la recopilación automática de la dirección IP en los eventos
  */
  sendDefaultPii: false, // <-- Esto es importante para proteger la privacidad de los usuarios. Asegura que no se envíe información personal identificable a Sentry a menos que se configure explícitamente.
  integrations: [
    Sentry.browserTracingIntegration(), //<--  Habilitar trazabilidad de navegación y rendimiento - Lentitud en la aplicación, navegación entre páginas, etc.
    Sentry.replayIntegration({
    // Máscara total por defecto - Esto asegura que no se capture ningún dato sensible, pero aún permite ver la estructura de la sesión y los eventos.
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
  }) //<-- Habilitar grabación de sesiones para reproducción de errores - Permite ver exactamente lo que el usuario hizo antes de un error, incluyendo clicks, navegación, etc.
  ],
  // Trazabilidad
  tracesSampleRate: 0.02, //<-- Captura el 2% de las transacciones
  // Establece 'tracePropagationTargets' para controlar en qué URLs se debe habilitar la trazabilidad distribuida
  tracePropagationTargets: SENTRY_TRACE_TARGETS,
  // Repetición de sesión
  replaysSessionSampleRate: 0, //<-- Esto establece la tasa de muestreo en 1%. En desarrollo puede ser 100% y luego muestrear a una tasa menor en producción.
  replaysOnErrorSampleRate: 0, //<-- Si no se esta muestreando toda la sesión, cambiar la tasa de muestreo a 100% cuando se muestreen sesiones donde ocurren errores.
  enableLogs: true, //<-- Habilitar el envío de logs a Sentry
  
  ignoreErrors: [
    'top.GLOBALS',
    'ResizeObserver loop limit exceeded',
    'Script error.', //<-- Errores de dominios externos
    'Extension context invalidated', // Errores de extensiones de Chrome
    'Error: Network Error', //<-- ignorar micro-cortes de internet del usuario
  ],
  beforeSend(event, hint) {
    errorCount++;
    // Si la app entra en un bucle de errores, detener el envío tras 10 errores
    // por sesión para no inundar el servidor.
    if (errorCount > 10) {
      return null; // Cancela el envío a Sentry
    }
    
    // Si el evento tiene información del usuario, borramos la IP por seguridad
    if (event.user) delete event.user.ip_address;

    return event;
  },

  // Definir el entorno
  environment: import.meta.env.MODE || 'development',
});

/*` Importar configuración de i18next */
import './i18n/config';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false
    }
  }
});

/**
 * Componente que crea el router después de que los providers estén disponibles
 */
const AppRouter = () => {
  const router = useMemo(() => createRouter(), []);

  return (
    <MaintenanceProvider>
      <MaintenanceWrapper>
        <RouterProvider router={router} />
      </MaintenanceWrapper>
    </MaintenanceProvider>
  );
};

// Registro del Service Worker para la PWA
registerSW({
  onOfflineReady() {
    console.log('El Portal Judicial ya puede funcionar sin conexión.');
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={AppCrashFallback} showDialog>
      <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={Theme}>
        <I18nProvider>
          <AuthProvider>
            <NotificationProvider>
              <AppRouter />
            </NotificationProvider>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
